import {
  maritalStatusOptions,
  jointPropertyRegimeOptions,
  defaultOccupations,
  specializationsOptions,
  booleanOptions,
  ethnicityOptions,
} from '@/constants/options'
import { ethnicDisclaimerText } from '@/pages/hiring/main-partner/defaults'
import { Autocomplete, Box, Skeleton, Stack, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { AddressForm } from '@/components'
import { useEffect } from 'react'
import { TextInput, SelectInput, RadioGroup } from '@/components/inputs'

type PartnerFormProps = {
  isMainPartner?: boolean
  isLoading: boolean
}

export const PartnerForm = ({
  isMainPartner = false,
  isLoading,
}: PartnerFormProps) => {
  const { control, watch, setValue, setError, clearErrors } = useFormContext()

  const [profession, marital_status, specializations, joint_property_regime] =
    watch([
      'profession',
      'marital_status',
      'specializations',
      'joint_property_regime',
    ])

  useEffect(() => {
    if (
      profession === 'Médico(a)' &&
      (!specializations || specializations.length === 0)
    ) {
      setError('specializations', {
        type: 'manual',
        message: 'Especialização é obrigatória para médicos',
      })
    } else {
      clearErrors('specializations')
    }
  }, [profession, specializations])

  useEffect(() => {
    if (['married', 'stable_union'].includes(marital_status)) {
      if (!joint_property_regime) {
        setError('joint_property_regime', {
          type: 'manual',
          message: 'Regime de bens é obrigatório para casados ou união estável',
        })
      } else {
        clearErrors('joint_property_regime')
      }
    }

    if (['single', 'divorced', 'widowed'].includes(marital_status)) {
      setValue('joint_property_regime', '')
      clearErrors('joint_property_regime')
    }
  }, [marital_status, joint_property_regime])

  return (
    <Stack pt={2} spacing={2}>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <TextInput
            control={control}
            name="name"
            label="Nome Completo *"
            placeholder="Nome completo (conforme documento de identidade) *"
          />

          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <TextInput name="rg" control={control} label="RG *" />
            <TextInput
              disabled={isMainPartner}
              name="tax_id"
              control={control}
              label="CPF *"
              mask={'000.000.000-00'}
            />
          </Stack>

          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <SelectInput
              name="marital_status"
              control={control}
              label="Estado Civil *"
              options={maritalStatusOptions}
            />

            <SelectInput
              name="joint_property_regime"
              control={control}
              label="Regime de união de bens *"
              options={jointPropertyRegimeOptions}
              disabled={
                marital_status !== 'married' &&
                marital_status !== 'stable_union'
              }
            />
          </Stack>

          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <TextInput
              name="county_of_birth"
              control={control}
              label="Município de Nascimento *"
            />
            <TextInput
              name="nationality"
              control={control}
              label="Nacionalidade *"
            />
          </Stack>

          <Stack direction={{ md: 'row' }} gap={2} justifyItems={'center'}>
            <SelectInput
              name="profession"
              control={control}
              label="Profissão *"
              options={defaultOccupations}
              sx={{ width: { md: '50%' } }}
            />

            {!['Médico(a)', 'Outro'].includes(profession) && (
              <Box width={{ md: '50%' }} mt={-2} />
            )}

            {profession === 'Outro' && (
              <TextInput
                name="otherProfession"
                control={control}
                label={
                  isMainPartner
                    ? 'Digite sua profissão'
                    : 'Digite a profissão do sócio'
                }
                disabled={profession !== 'Outro'}
                sx={{ width: { md: '50%' } }}
              />
            )}

            {profession === 'Médico(a)' && (
              <Controller
                name={'specializations'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    multiple
                    limitTags={1}
                    value={field.value || []}
                    options={specializationsOptions}
                    onChange={(_, value) => setValue('specializations', value)}
                    sx={{ width: { md: '50%' } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Especializações"
                        error={Boolean(error)}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            )}
          </Stack>

          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <TextInput
              name="crm"
              control={control}
              label="Número do Registro Profissional (ex. CRM)"
            />
            <TextInput
              name="voter_registration"
              control={control}
              label="Nº do Título de Eleitor"
            />
          </Stack>

          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <TextInput
              name="income_tax"
              control={control}
              label="Número do recibo da última declaração de IR"
            />
            <TextInput
              name="pis"
              control={control}
              label="Número do PIS (ou NIT)"
            />
          </Stack>

          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <TextInput
              name="phone"
              control={control}
              label="Telefone *"
              mask={'(00) 00000-0000'}
            />
            <TextInput
              name="email"
              control={control}
              label="E-mail *"
            />
          </Stack>

          <AddressForm />

          <RadioGroup
            name="has_cnh"
            isBooleanChoice
            control={control}
            label="Possui CNH? *"
            options={booleanOptions}
            row
          />

          <RadioGroup
            name="government_employee"
            isBooleanChoice
            control={control}
            label="É servidor público? *"
            options={booleanOptions}
            row
          />

          <RadioGroup
            name="contributor_inss"
            isBooleanChoice
            control={control}
            label="Trabalha em outro serviço em que há contribuição de INSS? *"
            options={booleanOptions}
            row
          />

          <RadioGroup
            name="is_other_company_partner"
            isBooleanChoice
            control={control}
            label="Você é sócio/quotista com mais de 10% em outro CNPJ? *"
            options={booleanOptions}
            row
          />

          <RadioGroup
            name="race"
            control={control}
            label="Autodeclaração étnico-racial*"
            options={ethnicityOptions}
            disclaimerText={ethnicDisclaimerText}
          />
        </>
      )}
    </Stack>
  )
}

const Loading = () => {
  return (
    <Box>
      <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
      <Stack direction={'row'} gap={2} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={56} width={'50%'} />
        <Skeleton variant="rounded" height={56} width={'50%'} />
      </Stack>
      <Stack direction={'row'} gap={2} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={56} width={'50%'} />
        <Skeleton variant="rounded" height={56} width={'50%'} />
      </Stack>
      <Stack direction={'row'} gap={2} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={56} width={'50%'} />
        <Skeleton variant="rounded" height={56} width={'50%'} />
      </Stack>
      <Skeleton variant="rounded" height={56} width={'50%'} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
    </Box>
  )
}
